import { Attribute, Relationship, Resource } from 'json-api';
import { AtomResource } from '../classes/atom-resource';
import { Country } from './country.resource';
import { NumberFilter, StringFilter } from 'src/app/filter/filter';
import { Job } from './job.resource';
import { AtomService } from '../atom.service';
import { Enum } from './enum.resource';
import { Firm } from './firm.resource';
import { Product } from './product.resource';
import { Keyword, KeywordsConfig } from './keyword.ressource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import {
  CountryConfig,
  EndUserProviderJobConfig,
  FirmConfig,
  ProductConfig,
} from 'src/app/shared/tools/search/search-config';

@Resource({
  type: 'shortcodes',
})
export class Shortcode extends AtomResource {
  @Attribute() shortcode?: string;
  @Attribute({ key: 'type' }) shortcodeType?: Enum;
  @Attribute() data?: Record<string, unknown>;

  @Relationship() enduserproviderjob?: Job;
  @Relationship() country?: Country;
  @Relationship() products?: Product[];
  @Relationship() ownerfirm?: Firm;
  @Relationship() keywords?: Keyword[];
}
export class ShortcodesConfig implements ResourceConfig {
  tables = {
    includes: [
      'enduserproviderjob.firm.headquarterscountry',
      'enduserproviderjob.activity',
      'country',
      'ownerfirm.headquarterscountry',
      'products',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        header: 'ID',
        translatable: false,
        displayed: true,
      },
      {
        columnDef: 'shortcode',
        displayedDatas: [
          { path: ['shortcode'], class: 'main', translatable: false },
        ],
        header: 'Shortcode',
        translatable: false,
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'shortcode',
      },
      {
        columnDef: 'shortcodeType',
        displayedDatas: [
          { path: ['shortcodeType'], class: 'main', translatable: false },
        ],
        header: 'Type',
        translatable: false,
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'type',
      },
      {
        columnDef: 'ownerfirm.name',
        displayedDatas: [
          {
            path: ['ownerfirm.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['ownerfirm.headquarterscountry.id'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Owner Firm',
        translatable: true,
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'ownerfirm.id',
          icon: 'business',
        },
        sort: 'ownerfirm.name',
      },
      {
        columnDef: 'enduserproviderjob.firm.name',
        displayedDatas: [
          {
            path: [
              'enduserproviderjob.firm.name',
              'enduserproviderjob.firm.headquarterscountry.id',
            ],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['enduserproviderjob.activity.name'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Enduser Provider Job - Firm',
        translatable: true,
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'enduserproviderjob.firm.id',
          icon: 'signal_cellular_alt',
        },
        sort: 'enduserproviderjob.firm.name',
      },
      {
        columnDef: 'country.name',
        displayedDatas: [
          { path: ['country.name'], class: 'mat-body-2', translatable: true },
          { path: ['country.id'], class: 'mat-body-1', translatable: false },
        ],
        header: 'Country',
        translatable: true,
        displayed: true,
        link: {
          resource: 'countries',
          filter: 'country.id',
          icon: 'language',
        },
        options: {
          flag: 'country.id',
        },
        sort: 'country.name',
      },
      {
        columnDef: 'keywords',
        header: 'Keywords',
        translatable: true,
        displayed: true,
        link: {
          resource: 'keywords',
          list: 'keywords',
          target: 'shortcode',
        },
        sort: 'keywords',
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'dialpad',
      title: [
        {
          path: ['shortcode'],
          translatable: false,
        },
      ],
      id: true,
    },
    elements: [
      {
        showEmpty: true,
        columns: true,
        list: [
          {
            icon: 'dialpad',
            title: 'Type',
            APIKey: 'shortcodeType',
            value: {
              path: ['shortcodeType'],
              translatable: false,
            },
          },
          {
            icon: 'language',
            title: 'Country',
            APIKey: 'country',
            value: {
              path: ['country.name'],
              translatable: true,
            },
            link: {
              resource: 'countries',
              filter: 'country.id',
            },
            options: {
              flag: 'country.id',
            },
          },
          {
            icon: 'signal_cellular_alt',
            title: 'Enduser Provider Job - Firm',
            APIKey: 'enduserproviderjob',
            value: {
              path: [
                'enduserproviderjob.firm.name',
                'enduserproviderjob.firm.headquarterscountry.id',
                'enduserproviderjob.activity.name',
              ],
              translatable: true,
            },
            link: {
              resource: 'firms',
              filter: 'enduserproviderjob.firm.id',
            },
          },
          {
            icon: 'business',
            title: 'Owner Firm',
            APIKey: 'ownerfirm',
            value: {
              path: ['ownerfirm.name'],
              translatable: true,
            },
            link: {
              resource: 'firms',
              filter: 'ownerfirm.id',
            },
          },
          {
            icon: 'public',
            title: 'Products',
            type: 'chips',
            APIKey: 'products',
            value: {
              path: ['name'],
              translatable: true,
            },
            link: {
              resource: 'products',
              filter: 'product.id',
            },
          },
          {
            icon: 'abc',
            title: 'Keywords',
            APIKey: 'keywords',
            value: {
              path: ['keyword'],
              translatable: false,
            },
            link: {
              resource: 'keywords',
              list: 'keywords',
              target: 'shortcode',
            },
          },
        ],
      },
    ],
    tabs: [
      {
        title: 'Keywords',
        elements: [
          {
            resource: {
              APIKey: 'keywords',
              config: new KeywordsConfig(this.atom),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'data',
        elements: [
          {
            title: 'Data',
            type: 'data',
            APIKey: 'data',
            value: {
              path: ['data'],
              translatable: false,
            },
          },
        ],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'Shortcode',
        formGroup: 'shortcode',
        list: [
          {
            type: 'text',
            title: 'Shortcode',
            formControl: 'shortcode',
            APIKey: 'shortcode',
            required: true,
            check: 'like',
          },
          {
            type: 'enum',
            title: 'Type',
            formControl: 'shortcodeType',
            APIKey: 'shortcodeType',
            fieldKey: 'type',
            multiple: false,
            required: true,
            presetValue: 'SMS',
          },
          {
            type: 'resource',
            title: 'Owner Firm',
            formControl: 'ownerfirm',
            search: new FirmConfig(this.atom),
            required: true,
            noCols: true,
            hint: 'Digital Virgo by default',
          },
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            search: new CountryConfig(this.atom),
            required: true,
            noCols: true,
            activatingSequence: [
              {
                group: 'shortcode',
                control: 'enduserproviderjob',
                filters: {
                  filter: 'equal.firm.headquarterscountry.id',
                  fixFilter: { 'equal.activity.id': '2' },
                },
              },
              {
                group: 'shortcode',
                control: 'products',
                filters: {
                  filter: 'like.country.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Enduser Povider Job',
            formControl: 'enduserproviderjob',
            search: new EndUserProviderJobConfig(this.atom),
            required: true,
            check: 'equal',
            noCols: true,
          },
        ],
      },
      {
        title: 'Products',
        formGroup: 'products',
        list: [
          {
            type: 'resource',
            title: 'Products',
            formControl: 'products',
            search: new ProductConfig(this.atom, true),
            required: false,
            multiple: true,
            noCols: true,
          },
        ],
      },
      {
        title: 'Data',
        formGroup: 'data',
        list: [
          {
            type: 'data',
            title: 'Data',
            formControl: 'data',
            required: false,
          },
        ],
      },
    ],
  };
  resource = Shortcode;
  resourceName = 'Shortcode';
  resourceKey = 'shortcodes';
  includes = [
    'enduserproviderjob.firm.headquarterscountry',
    'enduserproviderjob.activity',
    'country',
    'ownerfirm',
    'products',
    'keywords',

    'keywords.shortcode',
    'keywords.ownerfirm.headquarterscountry',
    'keywords.shortcode.enduserproviderjob.firm.headquarterscountry',
    'keywords.keywordrulesdvp4m.pack',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('shortcode', 'Shortcode'),
    new StringFilter('enduserproviderjob.firm.name', 'Enduser Provider Name'),
    new StringFilter('country.name', 'Country Name'),
    new StringFilter('country.id', 'Country ID', true),
    new StringFilter('ownerfirm.name', 'Owner Firm Name'),
    new NumberFilter('ownerfirm.id', 'Owner Firm ID'),
    new StringFilter('products.name', 'Product Name'),
    new NumberFilter('products.id', 'Product ID'),
    new StringFilter('keywords.keyword', 'Keyword'),
  ];
  constructor(private atom: AtomService) {}
}
